import { reactive, toRefs } from 'vue'
import { defineStore } from 'pinia'
import { useUrlSearchParams } from '@vueuse/core'
import {
  deepCopy,
  openSpecialDetails,
  openDetailsNews,
  trim,
  isMobileDevice,
  isWeChat
} from '@/utils/util'
import { setStorage } from '@/utils/storage'
import { getDetails } from '@/services/picDetails'
import { isBuild, isDev } from '@/utils/env'
import useEditorContent from '@gx-admin/hooks/web/useEditorContent'
import useWxShare from '@/hooks/web/useWxShare'
import { useStorePoint } from './point'
import { isArray } from '@/utils/validate'
import { cloneDeep } from 'lodash-unified'

/**
 * @Author      gx12358
 * @DateTime    2022/1/11
 * @lastTime    2022/1/11
 * @description store-global 全局属性
 */
export interface GlobalState {
  name?: string // app token
  liveId: string
  wechatTagReady: boolean
  wechatModal: boolean
  liveInfo: any
  initContentState: 0 | 1 | 2
  details: BffContentDetails
  scrollNum: number
  liveStatus: 'wait' | 'running' | 'end' | 'canctel' | 'paused'
  chatData: {
    id: string
    receivedTime: any
    content: string
    name: string
    avatar: string
    pictureUrls: string[]
  }[]
  channelData: ChannelList[]
}

type GlobalStateKey = keyof GlobalState

const bodyElement = document.querySelector('body')
const contentId = bodyElement?.getAttribute('data-id')
const objectType: NewsTypes = bodyElement?.getAttribute('data-objectType') as NewsTypes
const title = document.querySelector('#newsTitle')?.textContent || ''
const articleContentEl = document.querySelector('#newContent')

const articleContentText = articleContentEl?.innerHTML ? trim(articleContentEl?.innerHTML) : ''

export const useStoreGlobal = defineStore('global', () => {
  const { wxShare, wxShareV2 } = useWxShare()
  const { initEditorStr } = useEditorContent(isMobileDevice() ? 'mobile' : 'pc')
  const point = useStorePoint()
  const paramsUrls = useUrlSearchParams()
  const state = reactive<GlobalState>({
    name: '',
    liveStatus: (bodyElement?.getAttribute('data-live-status') as any) || 'wait',
    wechatTagReady: true,
    wechatModal: false,
    initContentState: 0,
    scrollNum: 0,
    liveId: bodyElement?.getAttribute('data-id'),
    liveInfo: {},
    details: {} as unknown as any,
    chatData: [],
    channelData: []
  })
  let openapp =
    (paramsUrls.openapp as 'true' | 'false') || (paramsUrls.promote == 'true' ? 'false' : 'true')
  openapp = ['true', 'false'].includes(openapp)
    ? openapp
    : paramsUrls.promote == 'true'
    ? 'false'
    : 'true'
  const openappRef = ref(openapp)
  const promote = ref(paramsUrls.promote)
  const initDetails = ref(false)

  const articleImageShow = reactive({
    open: false,
    imageList: [],
    active: 0
  })

  const setGlobalData: (
    params: Partial<Record<GlobalStateKey, GlobalState[GlobalStateKey]>>
  ) => void = (params) => {
    Object.assign(state, deepCopy(params))
  }

  const addChatData = (
    params: {
      id: string
      receivedTime: any
      content: string
      name: string
      avatar: string
      pictureUrls: string[]
    },
    type: 'unshift' | 'push' = 'push'
  ) => {
    if (type === 'push') state.chatData.push(params)
    if (type === 'unshift') state.chatData.unshift(params)
  }

  const filterChatData = () => {
    state.chatData = state.chatData.filter((el) => el.name && el.receivedTime)
  }

  const publicOpenDetails = (
    record: NewsItem,
    recommond: boolean,
    type: 'topic' | 'channel' = 'channel'
  ) => {
    if (isMobileDevice() && location.href.includes('isIner=0')) {
      point.contentClick(getPointData(record as any, recommond) as any)
      return
    }
    if (record.topicTemplate) {
      openSpecialDetails(record.topicPageName, record.objectId, record.topicTemplate, {
        trance: recommond && !!record.traceId,
        sceneId: record.sceneId || '',
        traceId: record.traceId || '',
        traceInfo: record.traceInfo || ''
      })
    } else {
      if (record.objectType === 10) {
        window.open(record.linkUrl)
      } else {
        const channelInfo = state.channelData.find(
          (item) => item.channelId == (record.mainChannel?.channelId || 0)
        )
        const channelAlias = channelInfo?.channelAlias || record.mainChannel?.channelId
        openDetailsNews(
          type === 'channel' ? channelAlias : record.mainTopicInfo?.topicId || '',
          record.objectId,
          type,
          type === 'topic' ? 1 : channelInfo?.channelAlias ? 2 : 1,
          {
            trance: recommond || !!record.traceId,
            traceId: record.traceId || '',
            traceInfo: record.traceInfo || '',
            sceneId: record.sceneId || ''
          }
        )
      }
    }

    point.contentClick(getPointData(record as any, recommond) as any)
  }

  const getPointData = (
    record: NewsItem & {
      pageName?: string
    },
    trace?: boolean
  ) => {
    return {
      pageName: record.pageName || '',
      pageId: record?.mainChannel?.pageId,
      newsType: record.objectType,
      newsId: record.objectId,
      appstyle: record.appStyle,
      link_url: record.linkUrl,
      live_type: !!record.liveInfo
        ? (global.live_type_change as any)[record.liveInfo?.liveState as any] || ''
        : '',
      live_stream_type: !!record.liveInfo ? (record.liveInfo?.vrType === 1 ? '2' : '1') : '',
      live_mode:
        !!record.liveInfo && record?.liveInfo?.tplId
          ? record.liveInfo?.tplId == '6'
            ? '2'
            : '1'
          : '',
      vlive_id:
        !!record.liveInfo && record.objectType === 2
          ? record.liveInfo?.vlive?.[0]?.vliveId || record.objectId
          : '',
      vlive_name:
        !!record.liveInfo && record.objectType === 2
          ? record.liveInfo?.vlive?.[0]?.name || record.newsTitle
          : '',
      newsTitle: record.newsTitle,
      channelId: record?.mainChannel?.channelId,
      author_name: record.newsAuthor,
      summary_type:
        record.objectType === 5
          ? record?.topicTemplate === 1
            ? global.summary_type.normal_subject
            : record?.topicTemplate === 3
            ? 'thematic_subject'
            : record?.topicTemplate === 2
            ? 'author_subject'
            : ''
          : '',
      summary_id: record.objectType === 5 ? record.objectId : '',
      trace,
      sceneId: record.sceneId,
      traceId: record.traceId,
      traceInfo: record.traceInfo
    }
  }

  async function initDetailsFn(details: BffContentDetails) {
    state.details = cloneDeep(details)
    setStorage({
      key: 'globalDetails',
      value: state.details,
      originKey: true,
      encryption: false
    })
    if (document.getElementById('globalShare')) {
      document.getElementById('globalShare').setAttribute('class', 'share-block')
    }
    if (isMobileDevice() && isWeChat()) {
      await wxShare({
        title: state.details.shareInfo?.shareTitle,
        description: state.details.shareInfo?.shareSummary,
        picture: state.details.shareInfo?.shareCoverUrl
      })

      wxShareV2()
    }
    state.initContentState = 1
  }

  onMounted(async () => {
    if (initDetails.value) {
      return
    }

    initDetails.value = true
    if ((objectType == '8' || objectType == '13') && isBuild()) {
      initEditorStr()
    }
    if (paramsUrls.promote == 'true') {
      let description: DetailsJson['description']
      let channelRels: DetailsJson['channelRels']
      let shareDetails: DetailsJson['contentShare']
      let subjectList: DetailsJson['TopicRelVos']
      try {
        description = contentDetailsJson.description || title
      } catch (e) {
        description = title
      }
      try {
        shareDetails = shareInfo
      } catch (e) {
        shareDetails = {
          shareTitle: title,
          shareDescription: description || title,
          fullUrl: ''
        }
      }
      try {
        channelRels = contentDetailsJson.channelRels || []
      } catch (e) {
        channelRels = []
      }
      try {
        subjectList = contentDetailsJson.TopicRelVos || []
      } catch (e) {
        subjectList = []
      }
      initDetailsFn({
        authorList: [],
        newsId: contentId,
        newsType: objectType,
        newsTitle: title,
        newsContent: articleContentText,
        appstyle: '',
        shareInfo: {
          shareTitle: shareDetails.shareTitle,
          shareSummary: shareDetails.shareDescription,
          shareCoverUrl: shareDetails.fullUrl
        },
        channelList: channelRels.map((item) => ({
          pageId: '',
          channelId: item.channelId,
          channelName: item.channelName
        })),
        subjectList: subjectList.map((item) => ({
          pageId: '',
          topicId: item.topicId,
          topicName: item.topicName
        }))
      })
    } else {
      const response = await getDetails({ contentIds: contentId })
      if (response && response.data && isArray(response.data)) {
        await initDetailsFn(response.data?.[0] || {})
        if ((objectType == '8' || objectType == '13') && isDev()) {
          if (articleContentText) {
            initEditorStr()
          } else if (state.details && state.details.newsContent && articleContentEl) {
            articleContentEl.innerHTML = state.details.newsContent
            // articleContentEl.innerHTML = `<div id="cmsMainContent" class="m-content" data-id="1rXVeMydz32" data-mapiurl="https://api.cgtn.com/app" data-url="//ui.cgtn.com/ui/cgtn_app_v6"><p id="video_target_id2" class=""><video controls="" width="860" src="https://cdnpdcontent.aikan.pdnews.cn/pdbj-20240314/vod/content/output/acc0aee675d44a4f9fee7c3a2bf742ea_opt.mp4" type="video/mp4" poster="https://cdnpdcontent.aikan.pdnews.cn/pdhk-20240314/image/content/f5c1751c53d840088e5652645f66ba3d.jpeg?v=45"></video>​</p><div class="text  en"><p>The China Manned Space Agency said on Wednesday that it had concluded the selection of a fourth batch of astronauts to join future space missions.</p><p>China planned to choose a total of 12 to 14 trainee astronauts to join its pool of astronauts for future spaceflight missions. The candidates include pilots, flight engineers and payload experts.</p><p>It is the first time that payload experts have been selected from Hong Kong and Macao, the two special administrative regions of China.</p><p>China started the selection process in 2022, and by August 2023, the second stage of the selection process was complete, with about 20 candidates entering the final round.</p><p>Trainee astronauts will go through intense training that normally lasts three and a half years before they are ready to conduct space missions.</p><p>China is planning two manned space missions in April and October this year, using the Long March-2F carrier rocket to send the Shenzhou-18 and Shenzhou-19 manned spacecraft to join up with the orbiting China Space Station.</p><p>Also in October, Tianzhou-8, an improved large-capacity cargo spacecraft, is scheduled to deliver supplies to the space station.</p></div><div class="sourceTextDiv"></div></div>`
            initEditorStr(state.details.newsContent || '')
          } else {
            if (document.querySelector('.skeleton-loading')) {
              document.querySelector('.skeleton-loading').classList.add('none')
            }
            const newContentEl = document.getElementById('newContent')
            if (newContentEl) {
              newContentEl.setAttribute(
                'class',
                isMobileDevice() ? 'news-content' : 'c-news-content'
              )
            }
          }
        }
      } else {
        state.initContentState = 2
        if ((objectType == '8' || objectType == '13') && isDev()) {
          initEditorStr()
        }
      }
    }

    point.contentExposure(Number(objectType) || 0)
    point.channelExposure(Number(objectType) || 0)
  })

  const getRouterParmas = () => {
    const utm_source_h5 = {
      article: '8',
      video: '1',
      live: '2',
      picture: '9',
      topic: '5',
      timeLineTopic: '5'
    }

    let utmParams = `&utm_source_h5=${
      utm_source_h5[paramsUrls.contentType as any]
    }&utm_wakeup_source=${isWeChat() ? 'Weixin' : '其他'}`

    const title = localStorage.getItem('enContentTitle')
    if (title) utmParams = `${utmParams}&utm_campaign_h5=${title}`

    if (paramsUrls.contentType === 'article' && paramsUrls.contentId) {
      return `?contentType=article&subType=h5_template_article&contentId=${paramsUrls.contentId}&skipType=1${utmParams}`
    } else if (paramsUrls.contentType === 'timeLineTopic' && paramsUrls.contentId) {
      return `?contentType=topic&subType=timeline_topic&contentId=${paramsUrls.contentId}&skipType=1${utmParams}`
    } else if (paramsUrls.contentType && paramsUrls.contentId) {
      return `?contentType=${paramsUrls.contentType}&contentId=${paramsUrls.contentId}&skipType=1${utmParams}`
    }
    return ''
  }

  const openApp = (type: 'google' | 'android' | 'ios', callBack?: () => void) => {
    if (callBack) {
      callBack()
    } else if (type === 'google') {
      if (isMobileDevice()) {
        location.href = 'https://play.google.com/store/apps/details?id=com.peopleapp.en'
      } else {
        window.open('https://play.google.com/store/apps/details?id=com.peopleapp.en')
      }
    } else if (type === 'android') {
      if (isMobileDevice()) {
        location.href = 'https://sj.qq.com/appdetail/com.peopleapp.en'
      } else {
        window.open('https://sj.qq.com/appdetail/com.peopleapp.en')
      }
    } else if (type === 'ios') {
      if (isMobileDevice()) {
        location.href =
          'https://apps.apple.com/app/peoples-daily-%E4%BA%BA%E6%B0%91%E6%97%A5%E6%8A%A5%E8%8B%B1%E6%96%87%E5%AE%A2%E6%88%B7%E7%AB%AF/id1294031865'
      } else {
        window.open(
          'https://apps.apple.com/app/peoples-daily-%E4%BA%BA%E6%B0%91%E6%97%A5%E6%8A%A5%E8%8B%B1%E6%96%87%E5%AE%A2%E6%88%B7%E7%AB%AF/id1294031865'
        )
      }
    }
  }

  const openWxChantApp = ({
    type,
    params,
    immediate,
    showModal,
    callBack
  }: {
    type: 'google' | 'android' | 'ios'
    params?: any
    showModal?: boolean
    immediate?: boolean
    callBack?: () => void
  }) => {
    if (isWeChat()) {
      if (showModal) {
        setGlobalData({
          wechatModal: true
        })
        return
      }
    }

    if (immediate) {
      openApp(type, callBack)

      return
    }
    const u = navigator.userAgent
    const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1 //安卓终端
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) //ios终端
    if (isAndroid) {
      // 安卓weibo的scheme协议跳转
      window.location.href = `peopledailyen://rmrb.app/openwith${params || getRouterParmas()}`
      setTimeout(function () {
        const hidden =
          window.document.hidden ||
          window.document.hidden ||
          // @ts-ignore
          window.document.mozHidden ||
          // @ts-ignore
          window.document.msHidden ||
          // @ts-ignore
          window.document.webkitHidden
        if (typeof hidden == 'undefined' || hidden == false) {
          openApp(type, callBack)
        }
      }, 3200)
    }
    // IOS下的scheme协议跳转
    if (isIOS) {
      window.location.href = `peopledailyen://rmrb.app/openwith${params || getRouterParmas()}`
      setTimeout(function () {
        const hidden =
          window.document.hidden ||
          window.document.hidden ||
          // @ts-ignore
          window.document.mozHidden ||
          // @ts-ignore
          window.document.msHidden ||
          // @ts-ignore
          window.document.webkitHidden
        if (typeof hidden == 'undefined' || hidden == false) {
          openApp(type, callBack)
        }
      }, 3200)
    }
  }

  const articleAudioPlayStatus = ref(false)

  return {
    ...toRefs(state),
    promote,
    openappRef,
    articleImageShow,
    openWxChantApp,
    publicOpenDetails,
    getPointData,
    setGlobalData,
    addChatData,
    filterChatData,
    articleAudioPlayStatus
  }
})
